import { Children, ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SyncOutlined } from '@ant-design/icons';
import { Access, AccessSource, EmployeeType } from '@prisma/client';
import { UseQueryResult } from '@tanstack/react-query';
import {
  Alert,
  App,
  AutoComplete,
  Button,
  ButtonProps,
  Checkbox,
  Form,
  FormItemProps,
  FormProps,
  ModalProps,
  Radio,
  ResultProps,
  Select,
  Space,
  SpaceProps,
  Spin,
  Typography,
} from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { uniq } from 'lodash';

import { SourceAccessItem } from '@app/domain-access-interfaces';
import { SubscriptionAccessWithChildren } from '@app/domain-subscription-interfaces';
import { CommonFindResult, NULL_PRODUCT_FAMILY_FEATURE } from '@app/infra-interfaces';
import {
  Axios,
  FeatureFlags,
  Modals,
  OptionCardProps,
  OptionCards,
  OptionCardsProps,
  Picker,
  SelectTable,
  Tables,
} from '@app/web-components';

import { AccessSelect, accessSelectColumns, isAccessEqual, useAccessSelectTable } from '../../common';
import { DependencyRenderer } from '../atoms/dependency-renderer';
import { ViewDependencies } from '../modals/view-dependencies';

import { useBundleNameAutoComplete } from './inputs/bundle-name';
import { ProductFamilyInput } from './inputs/product-family';
import { accessesMatchesEmployeeType } from './rules/access-matches-employee-type';
import { useNameIsUniqueRule } from './rules';

export type BundleFormData = {
  accessDependencies: { parent: SourceAccessItem | Access; child: SourceAccessItem | Access }[];
  accesses: (SourceAccessItem | Access)[];
  owners: DefaultOptionType[];
  employeeType?: EmployeeType;
  isActive: boolean;
  justificationRequired: boolean;
  name: string;
  planningStateValidationEnabled: boolean;
  productFamily?: DefaultOptionType;
  pullSubscriptionEnabled: boolean;
  pullSubscriptionRunning: boolean;
  anchors: (SourceAccessItem | Access)[];

  subscriptionAccesses?: number[];
};

export type Dependency = {
  child: SourceAccessItem | Access;
  parent: SourceAccessItem | Access;
};

const getIdentity = ({ externalId, accessSource }: SourceAccessItem | Access) => `${accessSource}-${externalId}`;

const _isCyclic = <NodeType extends SourceAccessItem | Access>(
  curr: NodeType,
  edges: { parent: NodeType; child: NodeType }[],
  visited: { [key: string]: boolean },
  inStack: { [key: string]: boolean },
) => {
  const identity = getIdentity(curr);

  if (inStack[identity]) {
    return true;
  }

  if (visited[identity]) {
    return false;
  }

  inStack[identity] = true;
  visited[identity] = true;

  for (const edge of edges.filter((edge) => isAccessEqual(edge.parent, curr))) {
    if (_isCyclic(edge.child, edges, visited, inStack)) {
      return true;
    }
  }

  inStack[identity] = false;
  return false;
};

const isCyclic = <NodeType extends SourceAccessItem | Access>(
  nodes: NodeType[],
  edges: { parent: NodeType; child: NodeType }[],
) => {
  const visited: { [key: string]: boolean } = {};
  const inStack: { [key: string]: boolean } = {};

  for (const node of nodes) {
    if (!visited[getIdentity(node)] && _isCyclic(node, edges, visited, inStack)) {
      return true;
    }
  }

  return false;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useBundleForm = (mutate: any | null, initialValues: BundleFormData, id?: number) => {
  const [accessDependencies, setAccessDependencies] = useState<Dependency[]>(initialValues.accessDependencies);
  const [anchors, setAnchors] = useState<(SourceAccessItem | Access)[]>(initialValues.anchors);
  const [child, setChild] = useState<SourceAccessItem | Access | undefined>(undefined);
  const [form] = Form.useForm<BundleFormData>();
  const { getProps: getAccessSelectTableProps } = useAccessSelectTable<SourceAccessItem | Access>();
  const { getProps: getBundleNameAutoCompleteProps } = useBundleNameAutoComplete();
  const { isFeatureEnabled } = FeatureFlags.useFeatureFlags();
  const modalProps = Modals.useDraggable({ children: `Manage Dependencies for ${child?.name}` });
  const nameIsUniqueRule = useNameIsUniqueRule();
  const navigate = useNavigate();
  const { notification } = App.useApp();

  const owners = Form.useWatch('owners', form);
  const { getSelectProps } = Picker.useWorkerPicker({
    setValue: (newValue) => form.setFieldValue('owners', newValue),
    value: owners ?? [],
    mode: 'multiple',
  });

  const accesses = Form.useWatch('accesses', form) ?? [];

  const getFormProps = (
    props?: FormProps<BundleFormData> & { children?: ReactNode },
  ): FormProps<BundleFormData> & { children: ReactNode } => ({
    children: (
      <>
        {mutate?.error ? <Form.Item {...getErrorMessageFormItemProps()} /> : undefined}
        <Form.Item {...getNameFormItemProps()} />
        <Form.Item {...getEmployeeTypeFormItemProps()} />
        <Form.Item {...getProductFamilyFormItemProps()} />
        <Form.Item {...getOwnersFormItemProps()} />
        <OptionCards {...getOptionsCardProps()} />
        <Form.Item {...getAccessListFormItemProps()} />
        {Children.map(props?.children, (x) => x)}
        <Space {...getFormButtonsSpaceProps()} />
      </>
    ),
    disabled: mutate?.isPending || mutate?.isSuccess || props?.disabled,
    form,
    initialValues,
    layout: 'vertical',
    style: { ...props?.style, width: '100%', minWidth: 1000 },
    onFinish: ({ accesses, owners, productFamily, ...values }) => {
      mutate?.mutate({
        ...values,
        accesses: accesses.map(({ externalId, accessSource }) => ({ externalId, accessSource })),
        productFamilyItemId: productFamily?.value,
        ownerEnterpriseIds: owners.map((x) => x.value),
        accessDependencies,
        pullSubscriptionRunning: initialValues.pullSubscriptionRunning,
        anchors,
      });

      props?.onFinish &&
        props.onFinish({
          ...values,
          accesses,
          accessDependencies,
          anchors,
          owners,
          productFamily,
          pullSubscriptionRunning: initialValues.pullSubscriptionRunning,
        });
    },
    onValuesChange: (changedValues, values) => {
      if (changedValues.accesses) {
        const newValue = accessDependencies.filter(
          (dependency) =>
            values.accesses.some((access) => isAccessEqual(dependency.child, access as Access)) &&
            values.accesses.some((access) => isAccessEqual(dependency.parent, access as Access)),
        );

        setAccessDependencies(newValue);
      }

      props?.onValuesChange && props.onValuesChange(changedValues, values);
    },
  });

  const getNameFormItemProps = (props?: FormItemProps): FormItemProps => ({
    ...props,
    children: <AutoComplete {...getBundleNameAutoCompleteProps()} autoFocus />,
    hasFeedback: true,
    label: 'Name',
    name: 'name',
    rules: [
      { required: true, message: 'Name is required.' },
      { min: 5, message: 'Name must be at least 5 characters.' },
      { max: 255, message: 'Name must be less than 255 characters.' },
      { validator: nameIsUniqueRule(id) },
      ...(props?.rules ?? []),
    ],
  });

  const getEmployeeTypeFormItemProps = (): FormItemProps => ({
    children: (
      <Radio.Group
        options={[
          { label: 'Intel Employee', value: EmployeeType.INTEL },
          { label: 'Contingent Worker', value: EmployeeType.CONTINGENT_WORKER },
        ]}
      />
    ),
    label: 'Employee Type',
    name: 'employeeType',
    rules: [{ required: true, message: 'Employee type is required.' }],
  });

  const _getProductFamilyFormItemProps_old = (): FormItemProps => ({
    children: <ProductFamilyInput />,
    label: 'Product Family',
    name: 'productFamily',
    rules: [{ required: true, message: 'Product Family is required.' }],
  });

  const _getProductFamilyFormItemProps_new = (): FormItemProps => ({
    children: <ProductFamilyInput placeholder="Type in Product Family (optional)" allowClear={true} />,
    label: 'Product Family',
    name: 'productFamily',
    help: (
      <div style={{ marginBottom: 20 }}>
        Product Family is used to control which products are evaluated during the CW eligibility check for AGS roles
        tied to processes. If it is omitted then only the process is used to determine eligibility.
      </div>
    ),
  });

  const getProductFamilyFormItemProps = (): FormItemProps =>
    isFeatureEnabled(NULL_PRODUCT_FAMILY_FEATURE)
      ? _getProductFamilyFormItemProps_new()
      : _getProductFamilyFormItemProps_old();

  const getOwnersFormItemProps = (): FormItemProps => ({
    children: <Select {...getSelectProps({ placeholder: 'Search Intel workers to set as owners' })} />,
    label: 'Owners',
    name: 'owners',
    rules: [{ required: true, message: 'At least 1 owner is required.' }],
  });

  const getRequireJustificationOptionCardProps = (): OptionCardProps => ({
    checkedHelp: 'Subscriber must provide justification.',
    children: <Form.Item label="Require Justification" name="justificationRequired" />,
    uncheckedHelp: 'Justification is optional for subscribers.',
  });

  const getEnablePullSubscriptionsOptionCardProps = (): OptionCardProps => ({
    checkedHelp: 'Automatically pull subscriptions from AGS and Active Directory.',
    children: (
      <Form.Item
        help={
          initialValues?.pullSubscriptionRunning ? (
            <Space>
              <Typography.Text type="warning">
                <SyncOutlined spin />
              </Typography.Text>
              <Typography.Text type="warning">Disabled while pull is running</Typography.Text>
            </Space>
          ) : undefined
        }
        label="Enable Pull (eager) Subscriptions"
        name="pullSubscriptionEnabled"
      >
        <Checkbox disabled={initialValues?.pullSubscriptionRunning} />
      </Form.Item>
    ),
    uncheckedHelp: 'Manually input subscriptions.',
  });

  const getEnableRemovalNotificationsOptionCardProps = (): OptionCardProps => ({
    checkedHelp: 'Automatically send removal request notifications when subscribers become ineligible.',
    children: <Form.Item label="Enable Removal Notifications" name="autoRemovalEnabled" />,
    uncheckedHelp: 'Take no action if subscribers become ineligible.',
  });

  const getBundleIsActiveOptionCardProps = (): OptionCardProps => ({
    checkedHelp: 'Bundle can be subscribed to.',
    children: <Form.Item label="Bundle Is Active" name="isActive" />,
    uncheckedHelp: 'Bundle is persisted but cannot be subscribed to.',
  });

  const getPlanningStateValidationEnabledOptionCardProps = (): OptionCardProps => ({
    checkedHelp:
      'Subscriptions to the accesses within this bundle will be subject to product family planning state validations.',
    children: <Form.Item label="Planning State Validation Enabled" name="planningStateValidationEnabled" />,
    uncheckedHelp:
      'Subscriptions to the accesses within this bundle will be exempt from product family planning state validations.',
  });

  const getOptionsCardProps = (): OptionCardsProps => {
    const items = [
      getRequireJustificationOptionCardProps(),
      getEnablePullSubscriptionsOptionCardProps(),
      getEnableRemovalNotificationsOptionCardProps(),
      getBundleIsActiveOptionCardProps(),
    ];

    if (isFeatureEnabled('planning_state_exempt')) {
      items.push(getPlanningStateValidationEnabledOptionCardProps());
    }

    return {
      label: 'Options',
      items,
    };
  };

  const getAccessListFormItemProps = (): FormItemProps => ({
    children: (
      <AccessSelect
        columns={[
          ...(isFeatureEnabled('eager_anchor')
            ? [
                {
                  dataIndex: 'isAnchor',
                  title: 'Anchor',
                  render: (_: unknown, record: SourceAccessItem | Access) => {
                    const isAnchor = anchors.some(
                      (x) => record.accessSource === x.accessSource && record.externalId === x.externalId,
                    );

                    return (
                      <Checkbox
                        checked={isAnchor}
                        onChange={(e) =>
                          setAnchors((prev) =>
                            e.target.checked
                              ? [...prev, record]
                              : prev.filter(
                                  (x) => x.accessSource !== record.accessSource || x.externalId !== record.externalId,
                                ),
                          )
                        }
                      />
                    );
                  },
                },
              ]
            : []),
          {
            dataIndex: 'dependencies',
            title: 'Dependencies',
            render: (_, record) => (
              <DependencyRenderer
                accessDependencies={accessDependencies}
                record={record}
                onClick={() => setChild(record)}
              />
            ),
          },
          ...accessSelectColumns,
        ]}
        footer={() =>
          accessDependencies.length && accesses?.length ? (
            <ViewDependencies accesses={accesses} accessDependencies={accessDependencies} />
          ) : undefined
        }
      />
    ),
    dependencies: ['employeeType'],
    label: 'Access List',
    name: 'accesses',
    rules: [
      { required: true, message: 'Access list is required.' },
      ({ getFieldValue }) => ({
        validator: accessesMatchesEmployeeType(getFieldValue('employeeType')),
      }),
    ],
  });

  const getSubscriptionAccessesFormItemProps = (
    queryResult: UseQueryResult<CommonFindResult<SubscriptionAccessWithChildren>, unknown>,
  ): FormItemProps => {
    const accessSourceFilters = [
      { text: 'AGS Entitlement', value: AccessSource.AGS_ENTITLEMENT },
      { text: 'AGS Role', value: AccessSource.AGS_ROLE },
      { text: 'IEM Group', value: AccessSource.IEM },
    ];

    const subAccesses =
      queryResult.data?.data.filter((x) =>
        accesses.some(
          (y) =>
            x.bundleAccess.access.accessSource === y.accessSource && x.bundleAccess.access.externalId === y.externalId,
        ),
      ) ?? [];

    return {
      children: queryResult.isFetching ? (
        <Spin style={{ width: '100%' }} />
      ) : (
        <SelectTable
          columns={[
            {
              title: 'User Name',
              dataIndex: ['subscription', 'user', 'name'],
              filters:
                uniq(subAccesses.map((x) => x.subscription.user.name))
                  .sort()
                  .map((x) => ({ text: x, value: x })) ?? [],
              onFilter: (value, record) => record.subscription.user.name === value,
            },
            {
              title: 'WWID',
              dataIndex: ['subscription', 'user', 'enterpriseId'],
              filters:
                uniq(subAccesses.map((x) => x.subscription.user.enterpriseId))
                  .sort()
                  .map((x) => ({ text: x, value: x })) ?? [],
              onFilter: (value, record) => record.subscription.user.enterpriseId === value,
            },
            {
              title: 'Access Source',
              dataIndex: ['bundleAccess', 'access', 'accessSource'],
              filters: accessSourceFilters,
              onFilter: (value, record) => record.bundleAccess.access.accessSource === value,
              render: (accessSource) => accessSourceFilters.find((x) => x.value === accessSource)?.text,
            },
            {
              title: 'Access Name',
              dataIndex: ['bundleAccess', 'access', 'name'],
              filters:
                uniq(subAccesses.map((x) => x.bundleAccess.access.name))
                  .sort()
                  .map((x) => ({ text: x, value: x })) ?? [],
              onFilter: (value, record) => record.bundleAccess.access.name === value,
            },
          ]}
          dataSource={subAccesses}
          pagination={false}
          size="small"
        />
      ),
      label: 'Subscription Accesses',
      name: 'subscriptionAccesses',
    };
  };

  const getManageDependenciesModalProps = (props?: ModalProps): ModalProps => {
    const handleOnChange = (newValue: (SourceAccessItem | Access)[]) => {
      if (!child) {
        return;
      }

      const newDependencies = [
        ...accessDependencies.filter((x) => !isAccessEqual(x.child, child)),
        ...newValue.map((x) => ({ child, parent: x })),
      ];
      if (isCyclic(accesses, newDependencies)) {
        notification.error({ message: 'Cannot add a cyclical dependency.' });
      } else {
        setAccessDependencies(newDependencies);
      }
    };

    return {
      ...props,
      ...modalProps,
      centered: true,
      children: (
        <>
          <p>The following selected accesses must be granted before {child?.name} can be requested.</p>
          <Tables.SelectTable
            {...getAccessSelectTableProps()}
            columns={accessSelectColumns}
            dataSource={accesses.filter((x) => !child || !isAccessEqual(x, child))}
            scroll={{ y: 460, x: 1000 }}
            value={accessDependencies.filter((x) => isAccessEqual(x.child, child)).map((x) => x.parent)}
            onChange={handleOnChange}
          />
        </>
      ),
      footer: [
        <Button key="close" type="primary" onClick={() => setChild(undefined)}>
          Close
        </Button>,
      ],
      open: !!child,
      styles: {
        ...props?.styles,
        body: {
          ...props?.styles?.body,
          height: 600,
        },
      },
      width: 1100,
      onCancel: (e) => {
        setChild(undefined);
        props?.onCancel && props.onCancel(e);
      },
    };
  };

  const getFormButtonsSpaceProps = (): SpaceProps => ({
    children: (
      <>
        <Button type="primary" htmlType="submit" loading={mutate?.isPending}>
          Save
        </Button>
        <Button onClick={() => form.resetFields()}>Reset</Button>
        <Button onClick={() => navigate('../')}>Cancel</Button>
      </>
    ),
  });

  const getErrorMessageFormItemProps = (): FormItemProps => ({
    children: <Alert message="Error" description={Axios.getErrorMessage(mutate.error)} type="error" showIcon />,
    colon: false,
    hidden: !mutate?.error,
    label: ' ',
  });

  const getCreateAnotherButtonProps = (): ButtonProps => ({
    children: 'Create Another',
    onClick: () => {
      mutate?.reset();
      form.resetFields();
    },
  });

  const getSuccessResultProps = (props?: ResultProps): ResultProps => ({
    ...props,
    extra: [
      <Button type="primary" key="go to bundles" onClick={() => navigate('../')}>
        Go To Bundles
      </Button>,
      ...(props?.extra ? Children.toArray(props?.extra) : []),
    ],
    status: 'success',
  });

  return {
    getAccessListFormItemProps,
    getBundleIsActiveOptionCardProps,
    getCreateAnotherButtonProps,
    getOwnersFormItemProps,
    getEmployeeTypeFormItemProps,
    getEnablePullSubscriptionsOptionCardProps,
    getEnableRemovalNotificationsOptionCardProps,
    getErrorMessageFormItemProps,
    getFormProps,
    getFormButtonsSpaceProps,
    getManageDependenciesModalProps,
    getNameFormItemProps,
    getOptionsCardProps,
    getPlanningStateValidationEnabledOptionCardProps,
    getProductFamilyFormItemProps,
    getRequireJustificationOptionCardProps,
    getSubscriptionAccessesFormItemProps,
    getSuccessResultProps,
  };
};
